import { defineStore } from 'pinia';
import * as api from '@/api';
import { platform } from '@/platform';
import { currentLocale } from '@/i18n';
import type { Me } from '@/types';

const TOKEN_KEY = 'emcity_guest_token';
const REFRESH_KEY = 'emcity_guest_refresh';

/**
 * Who the guest is (customer contour of the box). In Telegram we log in silently from initData every launch
 * (initData is short-lived and bound to the bot). Elsewhere (PWA) the stored token pair is used and phone-code
 * login is the fallback.
 */
// the start-up profile load: the router waits for it on a cold start (a link from the bot or a push opens a
// screen behind the login while the profile is still loading — the token alone is not «logged in» yet)
let started: Promise<void> = Promise.resolve();
export const sessionStarted = () => started;

export const useSessionStore = defineStore('session', {
  state: () => ({
    token: (platform.name === 'telegram' ? null : localStorage.getItem(TOKEN_KEY)) as string | null,
    refresh: (platform.name === 'telegram' ? null : localStorage.getItem(REFRESH_KEY)) as string | null,
    me: null as Me | null,
    status: 'idle' as 'idle' | 'loading' | 'ready' | 'anonymous' | 'error',
  }),
  getters: {
    isLoggedIn: (s) => !!s.token && !!s.me,
  },
  actions: {
    init() {
      started = this.load();
      return started;
    },
    async load() {
      this.status = 'loading';
      try {
        const credential = platform.credential();
        if (credential?.kind === 'telegram') {
          this.setSession(await api.authTelegram(credential.initData));
          this.me = await api.fetchMe();
        } else if (this.token) {
          this.me = await api.fetchMe();
        }
        // Bot messages are rendered in the account language — keep it equal to the language the app is shown in.
        if (this.me && this.me.language !== currentLocale()) {
          this.me = await api.updateMe({ language: currentLocale() }).catch(() => this.me);
        }
        this.status = this.me ? 'ready' : 'anonymous';
      } catch {
        this.status = platform.name === 'telegram' ? 'error' : 'anonymous';
      }
    },
    setSession(session: api.Session) {
      this.token = session.access;
      this.refresh = session.refresh;
      this.me = session.user;
      this.status = 'ready';
      api.forgetFavorites();
      if (platform.name !== 'telegram') {
        localStorage.setItem(TOKEN_KEY, session.access);
        localStorage.setItem(REFRESH_KEY, session.refresh);
      }
    },
    /** A new token pair after 401: by the refresh token, in Telegram — by initData again. */
    async renew(): Promise<boolean> {
      try {
        const credential = platform.credential();
        const fresh = credential?.kind === 'telegram'
          ? await api.authTelegram(credential.initData)
          : this.refresh ? await api.refreshSession(this.refresh) : null;
        if (!fresh) return false;
        const me = this.me;
        this.setSession(fresh);
        if (me) this.me = me;
        return true;
      } catch {
        return false;
      }
    },
    async refreshMe() {
      if (this.token) this.me = await api.fetchMe();
    },
    async logout() {
      if (this.refresh) await api.logout(this.refresh);
      this.expire();
    },
    expire() {
      this.token = null;
      this.refresh = null;
      this.me = null;
      this.status = 'anonymous';
      api.forgetFavorites();
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
    },
  },
});
